import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/use-auth-compat';
import { isMocked } from '@/config/featureFlags';
import {
  listWishlist as mockList,
  toggleWishlist as mockToggle,
  removeFromWishlist as mockRemove,
  isSaved as mockIsSaved,
} from '@/mocks/wishlist';
import {
  getWishlist,
  addToWishlist,
  removeFromWishlist as apiRemove,
} from '@/services/wishlistService';
import type { WishlistItem } from '@/types/registeredStudent';

const USE_MOCK = isMocked('WISHLIST');

export function useWishlist() {
  const { user } = useAuth();
  const token = user?.access_token ?? '';
  const [items, setItems] = useState<WishlistItem[]>(() => USE_MOCK ? mockList() : []);
  const [loading, setLoading] = useState(!USE_MOCK);

  const refresh = useCallback(async () => {
    if (USE_MOCK) {
      setItems(mockList());
    } else if (token) {
      try {
        const data = await getWishlist(token);
        setItems(data);
      } catch { /* ignore — stale UI is acceptable */ } finally {
        setLoading(false);
      }
    }
  }, [token]);

  useEffect(() => {
    if (!USE_MOCK && token) {
      void refresh();
    } else if (!USE_MOCK && !token) {
      setLoading(false);
    }
  }, [token, refresh]);

  const toggle = useCallback(
    async (handle: string, name: string): Promise<boolean> => {
      if (USE_MOCK) {
        const saved = mockToggle(handle, name);
        setItems(mockList());
        return saved;
      }
      const alreadySaved = items.some((i) => i.teacherHandle === handle);
      if (alreadySaved) {
        await apiRemove(token, handle);
        setItems((prev) => prev.filter((i) => i.teacherHandle !== handle));
        return false;
      } else {
        const newItem = await addToWishlist(token, handle, name);
        setItems((prev) => [newItem, ...prev]);
        return true;
      }
    },
    [token, items],
  );

  const remove = useCallback(
    async (handle: string) => {
      if (USE_MOCK) {
        mockRemove(handle);
        setItems(mockList());
      } else {
        await apiRemove(token, handle);
        setItems((prev) => prev.filter((i) => i.teacherHandle !== handle));
      }
    },
    [token],
  );

  const isSaved = useCallback(
    (handle: string): boolean => {
      if (USE_MOCK) return mockIsSaved(handle);
      return items.some((i) => i.teacherHandle === handle);
    },
    [items],
  );

  return { items, loading, toggle, remove, refresh, isSaved };
}
